import {serverDomain} from "../constants";
import {DbCustomer, dbCustomerSchema, NonGBAPatientResult} from "sharedlibs";
import {z} from "zod";
import KeycloakService from "../KeycloakService";
import i18n from "i18next";

/**
 * Gets all customers
 */
export async function getCustomers(): Promise<DbCustomer[]> {
  let response;
  const token = KeycloakService.instance.getToken()
  response = await fetch(`${serverDomain}/customers`, {
    method: 'GET',
    headers: {
      "Authorization": `Bearer ${token}`
    }
  })
  const json = await response.json()
  return z.array(dbCustomerSchema).parse(json)
}

/**
 * Gets all the non-GBA groups for all customer
 */
export async function getNonGBAPatientsForAllCustomer(): Promise<NonGBAPatientResult[]> {
  const token = KeycloakService.instance.getToken()
  const response = await fetch(`${serverDomain}/customers/getNonGBAPatientsForAllCustomer`, {
    method: 'GET',
    headers: {
      "Authorization": `Bearer ${token}`
    }
  })

  return await response.json()
}

/**
 * Gets all the non-GBA groups for a specific customer
 *
 * @param customerId
 */
export async function getNonGBAPatientGroupsForCustomer(customerId: string) {
  const token = KeycloakService.instance.getToken()
  const response = await fetch(`${serverDomain}/customers/getNonGBA/${customerId}`, {
    method: 'GET',
    headers: {
      "Authorization": `Bearer ${token}`
    }
  })

  return await response.json()
}

/**
 * Gets selected customers by their customerID
 *
 * @param id
 */
export async function getCustomerById(id: string): Promise<DbCustomer> {
  const token = KeycloakService.instance.getToken()
  const response = await fetch(`${serverDomain}/customers/${id}`, {
    method: 'GET',
    headers: {
      "Authorization": `Bearer ${token}`
    }
  })
  const json = await response.json()
  return dbCustomerSchema.parse(json)
}

/**
 * Adds a customer to our DB
 *
 * @param customerObject
 */
export async function addCustomer(customerObject: DbCustomer) {
  const token = KeycloakService.instance.getToken()
  const response = await fetch(`${serverDomain}/customers`, {
    method: 'POST',
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${token}`
    },
    body: JSON.stringify(customerObject)
  })

  if (!response.ok) {
    throw new Error(`Could not add customer! HTTP status: ${response.status}`);
  }
}

/**
 * Updates a customer in our DB
 *
 * @param {Object} customerObject
 * @param {Object} customerObject.device - The customer object.
 * @param {string} customerObject.user - The user who has done the action.
 *
 * @return {Promise<void>}
 */
export async function updateCustomer(customerObject: DbCustomer): Promise<void> {
  const token = KeycloakService.instance.getToken()
  const response = await fetch(`${serverDomain}/customers`, {
    method: 'PUT',
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${token}`
    },
    body: JSON.stringify(customerObject)
  })

  if (!response.ok) {
    throw new Error(`Could not update customer! HTTP status: ${response.status}`);
  }
}

/**
 * Downloads an Excel file listing all patients for a given customer at a specific date.
 *
 * @param customerId
 * @param date YYYY-MM-DD format
 */
export async function downloadPatientHashExcel(customerId: string, date: string): Promise<Response> {
  const token = KeycloakService.instance.getToken()
  const response = await fetch(`${serverDomain}/customers/patient-hash-excel/${customerId}/${date}`, {
    method: 'GET',
    headers: {
      "Authorization": `Bearer ${token}`
    }
  })

  if (!response.ok) {
    const body = await response.json().catch(() => ({}))
    throw new Error(body.error ?? `Fehler beim Herunterladen der Patientenliste: ${response.status}`)
  }

  return response
}

export interface LowImplantsRow {
  customerId: string;
  customerNumber: string;
  name: string;
  street: string;
  postalCode: string;
  city: string;
  tmzActiveSince: string;
  pauseUntil: string;
  plannedDeactivationDate: string;
  eTin: string;
  biotronikCustomerNumber: string;
  externalSensors: number;
  implants: number;
}

export async function getLowImplantsData(date?: string): Promise<LowImplantsRow[]> {
  const token = KeycloakService.instance.getToken()
  const url = date ? `${serverDomain}/customers/low-implants?date=${date}` : `${serverDomain}/customers/low-implants`
  const response = await fetch(url, {
    method: 'GET',
    headers: {"Authorization": `Bearer ${token}`}
  })
  if (!response.ok) {
    throw new Error(i18n.t('report.errors.loadFailed', {status: response.status}))
  }
  return response.json()
}

/**
 * Downloads an Excel report of all customers with 15 or fewer implants.
 */
export async function downloadLowImplantsExcel(date?: string): Promise<Response> {
  const token = KeycloakService.instance.getToken()
  const url = date ? `${serverDomain}/customers/low-implants-excel?date=${date}` : `${serverDomain}/customers/low-implants-excel`
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      "Authorization": `Bearer ${token}`
    }
  })

  if (!response.ok) {
    throw new Error(`Fehler beim Herunterladen des Reports: ${response.status}`)
  }

  return response
}

/**
 * (Safe-) Deletes a customer in our DB
 *
 * Safe deleting is used to keep the customer in our DB for 30 more days after officially pressing delete,
 * in case the user wants to restore the data.
 *
 * @param customerId
 * @param safe
 */
/**
 * Returns all customers that currently have no salesperson assigned.
 */
export async function getUnassignedCustomers(): Promise<DbCustomer[]> {
  const token = KeycloakService.instance.getToken()
  const response = await fetch(`${serverDomain}/customers/unassigned`, {
    method: 'GET',
    headers: {"Authorization": `Bearer ${token}`}
  })
  if (!response.ok) {
    throw new Error(`Could not load unassigned customers! HTTP status: ${response.status}`)
  }
  return z.array(dbCustomerSchema).parse(await response.json())
}

/**
 * Returns the customers currently assigned to the given salesperson.
 */
export async function getCustomersForSalesperson(salespersonId: string): Promise<DbCustomer[]> {
  const token = KeycloakService.instance.getToken()
  const response = await fetch(`${serverDomain}/salesperson/${salespersonId}/customers`, {
    method: 'GET',
    headers: {"Authorization": `Bearer ${token}`}
  })
  if (!response.ok) {
    throw new Error(`Could not load assigned customers! HTTP status: ${response.status}`)
  }
  return z.array(dbCustomerSchema).parse(await response.json())
}

/**
 * Assigns a salesperson to a customer (or clears the assignment when
 * salespersonId is null).
 */
export async function assignSalespersonToCustomer(customerId: string, salespersonId: string | null): Promise<DbCustomer> {
  const token = KeycloakService.instance.getToken()
  const response = await fetch(`${serverDomain}/customers/${customerId}/salesperson`, {
    method: 'PUT',
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${token}`,
    },
    body: JSON.stringify({salespersonId}),
  })
  if (!response.ok) {
    const body = await response.json().catch(() => ({}))
    throw new Error(body.error ?? `Could not assign salesperson! HTTP status: ${response.status}`)
  }
  return dbCustomerSchema.parse(await response.json())
}

export async function deleteCustomer(customerId: string, safe = true) {
  const token = KeycloakService.instance.getToken()
  const url = safe ? `${serverDomain}/customers/saveDelete/${customerId}` : `${serverDomain}/customers/${customerId}`

  const response = await fetch(url, {
    method: 'DELETE',
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${token}`
    },
  })

  if (!response.ok) {
    throw new Error(`Could not ${safe ? "safely" : ""} delete customer! HTTP status: ${response.status}`);
  }
}

